/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useMemo, useState} from 'react'
import {LineChart} from '@/_metronic/partials/content/chart/LineChart'
import {
  TrendProduct,
  TrendTransaction,
} from '@/app/modules/report-trend-analysis/core/_models'
import {DashboardChartCardHeader, type DashboardChartMetric} from './DashboardChartCardHeader'

export type MonthlyMetric = DashboardChartMetric

type Props = {
  trendTransaction?: TrendTransaction[] | null
  trendProduct?: TrendProduct[] | null
  summarySales?: number
  summaryTransaction?: number
  summaryProduct?: number
  isLoading?: boolean
  noResult?: boolean
}

const SalesByMonthlyChart: FC<Props> = ({
  trendTransaction = [],
  trendProduct = [],
  summarySales = 0,
  summaryTransaction = 0,
  summaryProduct = 0,
  isLoading = false,
  noResult = false,
}) => {
  const [metric, setMetric] = useState<MonthlyMetric>('revenue')

  const categories = useMemo(() => {
    const source =
      metric === 'item' ? trendProduct : trendTransaction
    return (
      source?.map((item) =>
        item.month?.split('2023')[0]?.substring(0, 3)?.trim() || item.month || ''
      ) || []
    )
  }, [metric, trendTransaction, trendProduct])

  const series = useMemo(() => {
    if (metric === 'revenue') {
      return [
        {
          name: 'Revenue',
          data: trendTransaction?.map((item) => item.total_sales ?? 0) ?? [],
        },
      ]
    }
    if (metric === 'transaction') {
      return [
        {
          name: 'Transaction',
          data:
            trendTransaction?.map((item) => item.total_transaction ?? 0) ?? [],
        },
      ]
    }
    if (metric === 'item') {
      return [
        {
          name: 'Item',
          data: trendProduct?.map((item) => item.total_qty ?? 0) ?? [],
        },
      ]
    }
    // customer - no data, show zeros
    return [
      {
        name: 'Customer',
        data: trendTransaction?.map(() => 0) ?? [],
      },
    ]
  }, [metric, trendTransaction, trendProduct])

  const total = useMemo(() => {
    if (metric === 'revenue') return summarySales
    if (metric === 'transaction') return summaryTransaction
    if (metric === 'item') return summaryProduct
    return 0
  }, [metric, summarySales, summaryTransaction, summaryProduct])

  const isCurrency = metric === 'revenue'

  return (
    <div className='col-12 col-xl-6 mb-5 mb-xl-10'>
      <div className='card border-0 shadow-sm h-100'>
        <DashboardChartCardHeader
          subtitle='Monthly'
          metric={metric}
          onMetricChange={setMetric}
        />
        <div className='card-body'>
          <LineChart
            color={metric === 'revenue' ? 'primary' : 'success'}
            series={series}
            categories={categories}
            isCurrency={isCurrency}
            isLoading={isLoading || noResult}
          />
        </div>
      </div>
    </div>
  )
}

export {SalesByMonthlyChart}
